// UltraFlex Admin — DD Collections & File Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Download, RefreshCw, Building2, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDDFiles() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [tab, setTab] = useState('files');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchData(); }, [tab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'files') {
        const res = await fetch(`${API}/api/dd-files`, { headers });
        setFiles((await res.json()).items || []);
      } else {
        const res = await fetch(`${API}/api/dd-files/reconciliation`, { headers });
        setReconciliation(await res.json());
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateFiles = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/dd-files/generate`, { method: 'POST', headers, body: '{}' });
      const data = await res.json();
      alert(`Generated ${data.files_generated} DD file(s)`);
      fetchData();
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const downloadFile = async (fileId, ref) => {
    const res = await fetch(`${API}/api/dd-files/${fileId}/download`, { headers });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${ref}.txt`; a.click();
  };

  const StatusBadge = ({ status }) => {
    const colors = { generated: 'bg-blue-900/40 text-blue-400', submitted: 'bg-yellow-900/40 text-yellow-400', acknowledged: 'bg-green-900/40 text-green-400', rejected: 'bg-red-900/40 text-red-400' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-800 text-gray-400'}`}>{status}</span>;
  };

  return (
    <div className="space-y-6" data-testid="dd-files-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">DD Collections</h1>
          <p className="text-sm text-gray-400 mt-1">Direct Debit file generation and reconciliation per site</p>
        </div>
        <button onClick={generateFiles} disabled={generating} data-testid="generate-dd-btn"
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm">
          <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} /> Generate DD Files
        </button>
      </div>

      <div className="flex gap-2 border-b border-gray-800 pb-2">
        {['files', 'reconciliation'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 rounded-t text-sm font-medium ${tab === t ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            {t === 'files' ? 'DD Files' : 'Reconciliation'}
          </button>
        ))}
      </div>

      {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : tab === 'files' ? (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
              <th className="text-left p-3">Reference</th><th className="text-left p-3">Site</th>
              <th className="text-left p-3">Date</th><th className="text-right p-3">Records</th>
              <th className="text-right p-3">Amount</th><th className="text-center p-3">Status</th>
              <th className="text-center p-3">Actions</th>
            </tr></thead>
            <tbody>
              {files.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-600">No DD files generated yet</td></tr> :
                files.map(f => (
                  <tr key={f.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                    <td className="p-3 text-white font-mono text-xs">{f.file_reference}</td>
                    <td className="p-3 text-gray-300">{f.site_name} <span className="text-gray-600 text-xs">({f.site_code})</span></td>
                    <td className="p-3 text-gray-400">{f.collection_date}</td>
                    <td className="p-3 text-right text-gray-300">{f.record_count}</td>
                    <td className="p-3 text-right text-white font-medium">£{f.total_amount?.toFixed(2)}</td>
                    <td className="p-3 text-center"><StatusBadge status={f.status} /></td>
                    <td className="p-3 text-center">
                      <button onClick={() => downloadFile(f.id, f.file_reference)} data-testid={`download-${f.id}`}
                        className="text-blue-400 hover:text-blue-300"><Download className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : reconciliation && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">£{reconciliation.total_amount?.toFixed(2)}</div>
              <div className="text-xs text-gray-500 uppercase mt-1">Total Amount</div>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-400">£{reconciliation.total_collected?.toFixed(2)}</div>
              <div className="text-xs text-gray-500 uppercase mt-1">Collected</div>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-red-400">£{reconciliation.total_bounced?.toFixed(2)}</div>
              <div className="text-xs text-gray-500 uppercase mt-1">Bounced</div>
            </div>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-3">Site</th><th className="text-right p-3">Records</th>
                <th className="text-right p-3">Total</th><th className="text-right p-3">Collected</th>
                <th className="text-right p-3">Bounced</th><th className="text-right p-3">Pending</th>
              </tr></thead>
              <tbody>
                {(reconciliation.sites || []).map(s => (
                  <tr key={s.site_id} className="border-b border-gray-800/50">
                    <td className="p-3 text-white">{s.site_name} <span className="text-gray-600 text-xs">({s.site_code})</span></td>
                    <td className="p-3 text-right text-gray-300">{s.total_records}</td>
                    <td className="p-3 text-right text-white">£{s.total_amount?.toFixed(2)}</td>
                    <td className="p-3 text-right text-green-400">£{s.collected_amount?.toFixed(2)} ({s.collected_count})</td>
                    <td className="p-3 text-right text-red-400">£{s.bounced_amount?.toFixed(2)} ({s.bounced_count})</td>
                    <td className="p-3 text-right text-yellow-400">£{s.pending_amount?.toFixed(2)} ({s.pending_count})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
